import React, { useEffect } from 'react';

import { useInfiniteQueryAllInvoicesHook } from '@fd/customHooks/useInfiniteQueryAllInvoices';
import useQueryBankAccountStatusesHook from '@fd/customHooks/useQueryBankAccountStatusesHook';
import { AlertFullWidth } from '@flipdish/ui-library/components/molecules/Alert';
import Grid from '@mui/material/Grid';
import {
  propertyManagerRolePermission,
  propertyOwnerRolePermission,
} from 'components/RMS/utils/permissions';
import { type TranslateFunction, getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import { showPausedPayouts } from '../../selectors/app.selector';
import { permissionsSelector } from '../../selectors/permissions.selector';
import { useTracking } from '../../services/amplitude/useTracking';
import { PortalFeatureFlags } from '../../services/featureFlags/portalFeatureFlags';
import { useFeatureFlagValue } from '../../services/featureFlags/useFeatureFlagValue';
import { flagService } from '../../services/flagService';
import PageLayout from '../../ui/Layout';
import DynamicAlertBanner from '../../ui/molecules/DynamicAlertBanner/DynamicAlertBanner';
import Spacer from '../../ui/Spacer';
import { getBankAccounts } from '../Finance/Banking/banking.actions';
import { bankingSelectors } from '../Finance/Banking/banking.selectors';
import { useShouldShowFlipdishAppBanner } from '../FlipdishAppBanner/useShouldShowFlipdishAppBanner';
import { useHasAccessToOnboardingV3 } from '../OnboardingV3/hooks/useHasAccessToOnboardingV3';
import AlertBankVerification from './Configs/AlertBankVerification';
import AlertPayoutFailed from './Configs/AlertPayoutFailed';
import InviteTeamAlert from './Configs/InviteTeamAlert';
import SetupConfigList from './Configs/List';
import { useInviteTeamBannerDismissal } from './Configs/useInviteTeamBannerDismissal';
import { useIsOwnerOnlyOrg } from './Configs/useIsOwnerOnlyOrg';
import HomeLiveReportWidget from './LiveReport/HomeLiveReportWidget';
import Savings from './Savings/Savings';
import { getWelcomeTitle } from './selectors';
import StoreCardList from './Stores/List';

type InnerProps = MappedState & MappedDispatch;
type Props = InnerProps;

export const Home = (props: Props) => {
  const {
    appId,
    bankAccountAlerts,
    canEditTeammates,
    currentApp,
    hasBankingPermissions,
    hasInvoicePermissions,
    isAlertBannerInvoiceOn,
    isPayoutFailedBannerOn,
    loadSetup,
    orgId,
    showPausedPayouts = false,
    title,
    translate = ((id: string) => id) as TranslateFunction,
  } = props;
  const isFlipdishGlobal = currentApp.AppId === 'flipdish-global';
  const isLiveReportEnabled = useFeatureFlagValue(
    PortalFeatureFlags.liveOverviewReportHomepage,
    false
  );
  const { onboardingWaitingView } = useHasAccessToOnboardingV3();

  //#region PayoutFailedBanner
  const { hasFailedPayout } = useQueryBankAccountStatusesHook(
    orgId,
    appId,
    isPayoutFailedBannerOn && hasBankingPermissions && !isFlipdishGlobal
  );
  const showPayoutFailedBanner = isPayoutFailedBannerOn && !isFlipdishGlobal && hasFailedPayout;
  //#endregion

  useEffect(() => {
    if (hasBankingPermissions) {
      loadSetup();
    }
  }, [hasBankingPermissions]);

  //#region DynamicAlertBanner
  const { overdueInvoices } = useInfiniteQueryAllInvoicesHook({
    appId: appId,
    isEnabled: hasInvoicePermissions,
  });

  const showBanner =
    !onboardingWaitingView &&
    !isFlipdishGlobal &&
    isAlertBannerInvoiceOn &&
    !showPausedPayouts &&
    overdueInvoices?.length > 0;
  //#endregion

  const showAppDownloadBanner = useShouldShowFlipdishAppBanner();

  // Banking, app-download, and onboarding-waiting take precedence over the invite-team notice.
  const hasPrecedenceBanner =
    onboardingWaitingView ||
    (!isFlipdishGlobal && bankAccountAlerts.length > 0) ||
    showPayoutFailedBanner ||
    showPausedPayouts ||
    showBanner ||
    showAppDownloadBanner;

  const isEligibleViewer = canEditTeammates && !hasPrecedenceBanner;
  const { isDismissed, dismiss } = useInviteTeamBannerDismissal(isEligibleViewer);
  const isOwnerOnlyOrg = useIsOwnerOnlyOrg(appId, isEligibleViewer && !isDismissed);

  const showInviteTeamAlert = isEligibleViewer && !isDismissed && isOwnerOnlyOrg;

  const { trackEvent } = useTracking();

  useEffect(() => {
    trackEvent('portal_home', {
      action: 'logged_in',
    });
  }, []);

  return (
    <PageLayout
      title={title}
      pageHeader={showInviteTeamAlert ? <InviteTeamAlert onDismiss={dismiss} /> : undefined}
      fullWidth={onboardingWaitingView}
      noMaxWidth={onboardingWaitingView}
    >
      {onboardingWaitingView ? (
        <div data-tailwind>
          <AlertFullWidth
            color="warning"
            title={translate('Onboarding_In_Progress_Waiting_For_Owner') as string}
            description=""
            confirmLabel=""
          />
        </div>
      ) : (
        <Grid container>
          {!isFlipdishGlobal &&
            bankAccountAlerts.map((acc) => (
              <Grid item xs={12} key={acc.priority}>
                <AlertBankVerification account={acc} />
              </Grid>
            ))}
          {showBanner && (
            <DynamicAlertBanner
              backgroundColor="yellow"
              bannerText={'You_have_1_or_more_invoices_overdue_payment'}
              buttonText={'Go_to_invoices'}
              showButton={true}
              toUrl={`/${appId}/finance/invoices`}
            />
          )}
          {showPayoutFailedBanner && (
            <Grid item xs={12}>
              <AlertPayoutFailed />
            </Grid>
          )}
          {!isLiveReportEnabled && (
            <Grid item xs={12}>
              <Savings />
            </Grid>
          )}
          <HomeLiveReportWidget />
          <Grid item xs={12}>
            <SetupConfigList />
            <Spacer size={24} />
          </Grid>
          <Grid item xs={12}>
            <StoreCardList />
          </Grid>
        </Grid>
      )}
    </PageLayout>
  );
};

type MappedState = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
const mapStateToPropsFactory = () => {
  const getBankingPermissionsSelector = permissionsSelector.hasPermissionFactory([
    'Owner',
    'ManagedOwner',
    'FinanceManger',
    'FlipdishStaff',
  ]);
  const getInvoicePermissionsSelector = permissionsSelector.hasPermissionFactory([
    'Owner',
    'ManagedOwner',
    ...propertyManagerRolePermission,
    ...propertyOwnerRolePermission,
    'FinanceManger',
    'FlipdishStaff',
  ]);
  const canEditTeammatesSelector = permissionsSelector.hasPermissionFactory(['EditTeammates']);
  return (state: AppState) => {
    return {
      appId: state.currentApp.AppId!,
      bankAccountAlerts: bankingSelectors.getBankAccountAlerts(state),
      canEditTeammates: canEditTeammatesSelector(state),
      currentApp: state.currentApp,
      orgId: state.currentApp?.OrgId ?? '',
      isAlertBannerInvoiceOn: flagService.isFlagOn(state, 'alertBannerInvoice'),
      isPayoutFailedBannerOn: flagService.isFlagOn(state, 'payout_failed_banner'),
      showPausedPayouts: showPausedPayouts(state),
      title: getWelcomeTitle(state),
      hasBankingPermissions: getBankingPermissionsSelector(state),
      hasInvoicePermissions: getInvoicePermissionsSelector(state),
      translate: getTranslate(state.locale),
    };
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  loadSetup: () => {
    dispatch(getBankAccounts());
  },
});

export default compose<InnerProps, Record<string, never>>(
  connect(mapStateToPropsFactory, mapDispatchToProps)
)(Home);
